// Bottom-right Sparkles button that opens the chat panel (Task 4.2,
// AI_ASSISTANT_REMEDIATION_PLAN D-A8). Mounted once in AppLayout, so it
// automatically covers every resource page, every index page, and signed-in
// Community, and is absent for anonymous share-link visitors and marketing
// pages, all of which render without AppLayout.
//
// Gating comes from GET /ai/status (tier 0 renders nothing) rather than the
// admin-group check this used to do client-side; this is still cosmetic —
// the chat Lambda and the apply routes re-check access server-side (D-R1).
//
// The composer draft lives here, not in AiChatPanel: the panel unmounts on close,
// and closing it (Escape included) must never throw away what the user typed
// (site-assistant SA25).

import { useEffect, useRef, useState } from "react";
import { Sparkles } from "lucide-react";
import { useAuth } from "../../context/AuthContext.js";
import { useResourceContext } from "../../lib/aiChat/context.js";
import { fetchAiStatus, postAiOutcome } from "../../lib/api.js";
import { AiChatPanel } from "./AiChatPanel.js";
import type { AiAssistantStatus } from "@checklist/shared";

export function AiChatDock() {
  const { user } = useAuth();
  const context = useResourceContext();
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<AiAssistantStatus | null>(null);
  const [draft, setDraft] = useState("");
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Hooks above every early return, per the hazard note in the plan: a hooks-
  // order violation typechecks cleanly here (no ESLint in this workspace) and
  // only crashes at runtime.
  useEffect(() => {
    // A draft belongs to the person who typed it; a different sign-in starts empty.
    setDraft("");
    if (!user) {
      setStatus(null);
      return;
    }
    let cancelled = false;
    void fetchAiStatus()
      .then((s) => {
        if (!cancelled) setStatus(s);
      })
      .catch(() => {
        if (!cancelled) setStatus(null);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.sub]);

  function handleToggle() {
    const next = !open;
    setOpen(next);
    if (next) {
      void fetchAiStatus()
        .then(setStatus)
        .catch(() => undefined);
      const surface = context?.kind ?? "other";
      void postAiOutcome({ event: "open", surface });
    }
  }

  if (!user || !status || status.tier === 0) return null;

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={handleToggle}
        aria-label={open ? "Close AI assistant" : "Open AI assistant"}
        aria-expanded={open}
        className="fixed bottom-6 right-6 z-30 flex h-14 w-14 items-center justify-center rounded-full bg-brand-600 text-white shadow-lg transition hover:bg-brand-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40"
      >
        <Sparkles size={22} aria-hidden="true" />
      </button>
      {open ? (
        <AiChatPanel
          context={context}
          status={status}
          draft={draft}
          onDraftChange={setDraft}
          onClose={() => {
            setOpen(false);
            buttonRef.current?.focus();
          }}
        />
      ) : null}
    </>
  );
}
